/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import {
  Bell, Buildings, CalendarBlank, ChartLineUp,
  CheckSquare, ClockCounterClockwise, CurrencyEur, FileText, GearSix,
  IdentificationCard, LinkSimple, ListChecks, Package, ShieldCheck, ShoppingCart,
  UsersThree, Wrench
} from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

// REGLE DE GESTION V6.5 : tous les onglets texte recoivent un pictogramme fin et
// homogene quand l'appelant n'en fournit pas deja un. Cela evite de modifier une
// par une toutes les pages historiques de Planigo tout en preservant leurs icones.
const TAB_ICONS = {
  identity: IdentificationCard, identification: IdentificationCard, general: IdentificationCard,
  roles: ShieldCheck, rights: ShieldCheck, permissions: ShieldCheck, security: ShieldCheck,
  access: GearSix, terminals: GearSix, devices: GearSix,
  notifications: Bell, network: LinkSimple, filiere: LinkSimple,
  address: Buildings, payment: CurrencyEur, management: GearSix,
  contacts: UsersThree, catalog: Package, catalogue: Package, purchase: ShoppingCart,
  stock: Package, store: Package, technical: Wrench, procedures: ListChecks, links: LinkSimple,
  documents: FileText, history: ClockCounterClockwise, followup: ChartLineUp,
  planning: CalendarBlank, qa: CheckSquare, tests: CheckSquare, archive: Package,
  users: UsersThree,
}

function hasVisualChild(children) {
  const list = React.Children.toArray(children)
  if (list.length > 1) return true
  return list.some(child => React.isValidElement(child) && child.type !== React.Fragment)
}

const TabsTrigger = React.forwardRef(({ className, children, value, ...props }, ref) => {
  const key = String(value || '').toLowerCase()
  const Icon = TAB_ICONS[key] || GearSix
  const addIcon = !hasVisualChild(children)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      )}
      value={value}
      {...props}>
      {addIcon && <Icon size={13} weight="regular" className="mr-1.5 shrink-0 opacity-75" aria-hidden="true" />}
      {children}
    </TabsPrimitive.Trigger>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
